import React, { useState } from 'react';
import { ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';
import type { TrendPoint } from './types';

/** Sum of the second half of the window vs the first — a real, if coarse,
 *  direction-of-travel signal instead of an invented week-over-week figure. */
function trendChange(points: TrendPoint[]): { pct: number | null; isPositive: boolean } {
  if (!points || points.length < 2) return { pct: null, isPositive: true };
  const mid = Math.floor(points.length / 2);
  const firstHalf = points.slice(0, mid).reduce((s, p) => s + p.count, 0);
  const secondHalf = points.slice(mid).reduce((s, p) => s + p.count, 0);
  if (firstHalf === 0) return { pct: secondHalf > 0 ? 100 : null, isPositive: true };
  const pct = Math.round(((secondHalf - firstHalf) / firstHalf) * 100);
  return { pct, isPositive: pct >= 0 };
}

function AreaSparkline({
  points,
  strokeColor,
  fillGradientId,
  fillColor,
}: {
  points: TrendPoint[];
  strokeColor: string;
  fillGradientId: string;
  fillColor: string;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  if (!points || points.length === 0) {
    return (
      <p className="mt-2 text-body-sm text-on-surface-variant">
        Nothing was recorded in this window. The line stays empty rather than drawing a flat zero.
      </p>
    );
  }

  const width = 340;
  const height = 90;
  const paddingY = 10;
  const usableHeight = height - paddingY * 2;

  const maxVal = Math.max(...points.map((p) => p.count), 1);
  const minVal = 0;

  const stepX = width / (points.length - 1);
  const coords = points.map((p, i) => {
    const x = i * stepX;
    const y = height - paddingY - ((p.count - minVal) / (maxVal - minVal || 1)) * usableHeight;
    return { x, y, point: p };
  });

  const linePath = coords.reduce((acc, curr, idx, arr) => {
    if (idx === 0) return `M ${curr.x} ${curr.y}`;
    const prev = arr[idx - 1];
    const cpX = (prev.x + curr.x) / 2;
    return `${acc} C ${cpX} ${prev.y}, ${cpX} ${curr.y}, ${curr.x} ${curr.y}`;
  }, '');

  const areaPath = `${linePath} L ${coords[coords.length - 1].x} ${height} L ${coords[0].x} ${height} Z`;

  return (
    <div className="relative pt-2">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-24 overflow-visible"
        preserveAspectRatio="none"
        onMouseLeave={() => setHoverIndex(null)}
      >
        <defs>
          <linearGradient id={fillGradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fillColor} stopOpacity={0.28} />
            <stop offset="100%" stopColor={fillColor} stopOpacity={0.0} />
          </linearGradient>
        </defs>

        {/* Fill Area */}
        <path d={areaPath} fill={`url(#${fillGradientId})`} />

        {/* Smooth Curve */}
        <path d={linePath} fill="none" stroke={strokeColor} strokeWidth={2.5} strokeLinecap="round" />

        {/* Interaction hitpoints & points */}
        {coords.map((c, i) => (
          <g key={i} className="cursor-pointer" onMouseEnter={() => setHoverIndex(i)}>
            <circle
              cx={c.x}
              cy={c.y}
              r={hoverIndex === i ? 4.5 : 2}
              fill="#FFFFFF"
              stroke={strokeColor}
              strokeWidth={hoverIndex === i ? 2.5 : 1.5}
            />
            {/* Wider hit zone */}
            <rect
              x={c.x - stepX / 2}
              y={0}
              width={stepX}
              height={height}
              fill="transparent"
            />
          </g>
        ))}
      </svg>

      {/* Hover Tooltip Overlay */}
      {hoverIndex !== null && coords[hoverIndex] && (
        <div
          className="pointer-events-none absolute -top-5 z-20 -translate-x-1/2 rounded-md bg-inverse-surface px-2 py-1 text-data-xs font-semibold text-on-primary shadow-surface-1 whitespace-nowrap transition-all"
          style={{ left: `${(coords[hoverIndex].x / width) * 100}%` }}
        >
          {coords[hoverIndex].point.date}: {coords[hoverIndex].point.count}
        </div>
      )}

      {/* X-axis ticks */}
      <div className="mt-1 flex justify-between text-data-xs font-medium text-on-surface-subtle">
        <span>{points[0].date}</span>
        <span>{points[Math.floor(points.length / 2)].date}</span>
        <span>{points[points.length - 1].date}</span>
      </div>
    </div>
  );
}

export const SalesActivityTrends: React.FC<{
  enquiryPoints: TrendPoint[];
  quotationPoints: TrendPoint[];
}> = ({ enquiryPoints, quotationPoints }) => {
  const enquiryTotal = enquiryPoints.reduce((s, p) => s + p.count, 0);
  const quotationTotal = quotationPoints.reduce((s, p) => s + p.count, 0);
  const enquiryChange = trendChange(enquiryPoints);
  const quotationChange = trendChange(quotationPoints);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Enquiries Trend */}
      <div className="rounded-card border border-border bg-card p-5 shadow-xs">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-label-caps uppercase tracking-wider text-on-surface-variant">
              Enquiries created
            </span>
            <div className="mt-1 flex items-baseline gap-2.5">
              <span className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">{enquiryTotal}</span>
              {enquiryChange.pct !== null && (
                <span className={`inline-flex items-center text-body-sm font-semibold ${enquiryChange.isPositive ? 'text-success-text' : 'text-danger-text'}`}>
                  {enquiryChange.isPositive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                  {Math.abs(enquiryChange.pct)}%
                </span>
              )}
            </div>
          </div>

          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface-variant px-2 py-1 text-data-xs font-medium text-on-surface-subtle">
            <Calendar className="h-3 w-3" />
            <span>Last 10 days</span>
          </div>
        </div>

        <AreaSparkline
          points={enquiryPoints}
          strokeColor="#5b45d6"
          fillGradientId="enquiryGrad"
          fillColor="#5b45d6"
        />
      </div>

      {/* Quotations Trend */}
      <div className="rounded-card border border-border bg-card p-5 shadow-xs">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-label-caps uppercase tracking-wider text-on-surface-variant">
              Quotations created
            </span>
            <div className="mt-1 flex items-baseline gap-2.5">
              <span className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">{quotationTotal}</span>
              {quotationChange.pct !== null && (
                <span className={`inline-flex items-center text-body-sm font-semibold ${quotationChange.isPositive ? 'text-success-text' : 'text-danger-text'}`}>
                  {quotationChange.isPositive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                  {Math.abs(quotationChange.pct)}%
                </span>
              )}
            </div>
          </div>

          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface-variant px-2 py-1 text-data-xs font-medium text-on-surface-subtle">
            <Calendar className="h-3 w-3" />
            <span>Last 10 days</span>
          </div>
        </div>

        <AreaSparkline
          points={quotationPoints}
          strokeColor="#0ba5ec"
          fillGradientId="quotationGrad"
          fillColor="#0ba5ec"
        />
      </div>
    </div>
  );
};
